/**
 * Local storage utility for managing decks in offline-first mode
 */

const DECKS_STORAGE_KEY = 'riftbound-local-decks';

/**
 * Get all locally stored decks
 * @returns {Array} - Array of locally stored decks
 */
export const getLocalDecks = () => {
  try {
    const decksJSON = localStorage.getItem(DECKS_STORAGE_KEY);
    return decksJSON ? JSON.parse(decksJSON) : [];
  } catch (error) {
    console.error('Error reading local decks:', error);
    return [];
  }
};

/**
 * Save a deck to local storage
 * @param {Object} deck - The deck object to save
 * @returns {Object} - The saved deck with an ID
 */
export const saveLocalDeck = (deck) => {
  try {
    const existingDecks = getLocalDecks();
    const timestamp = new Date().toISOString();
    
    // If deck doesn't have an ID, create a new one
    const deckToSave = {
      ...deck,
      id: deck.id || `local-${Date.now()}`,
      localId: deck.localId || `local-${Date.now()}`,
      isLocal: true,
      createdAt: deck.createdAt || timestamp,
      updatedAt: timestamp
    };
    
    // Check if this is an update to an existing local deck
    const existingIndex = existingDecks.findIndex(d => d.localId === deckToSave.localId || d.id === deckToSave.id);
    
    if (existingIndex !== -1) {
      // Update existing deck
      existingDecks[existingIndex] = deckToSave;
    } else {
      // Add new deck
      existingDecks.push(deckToSave);
    }
    
    localStorage.setItem(DECKS_STORAGE_KEY, JSON.stringify(existingDecks));
    return deckToSave;
  } catch (error) {
    console.error('Error saving local deck:', error);
    return null;
  }
};

/**
 * Update an existing local deck
 * @param {Object} deck - The deck object with updated data
 * @returns {Object|boolean} - Updated deck object or false on failure
 */
export const updateLocalDeck = (deck) => {
  try {
    const existingDecks = getLocalDecks();
    const index = existingDecks.findIndex(d => d.localId === deck.localId || d.id === deck.id);
    
    if (index !== -1) {
      const timestamp = new Date().toISOString();
      const updatedDeck = {
        ...deck,
        updatedAt: timestamp
      };
      
      existingDecks[index] = updatedDeck;
      localStorage.setItem(DECKS_STORAGE_KEY, JSON.stringify(existingDecks));
      return updatedDeck;
    }
    
    return false;
  } catch (error) {
    console.error('Error updating local deck:', error);
    return false;
  }
};

/**
 * Delete a local deck
 * @param {string} deckId - The ID of the deck to delete
 * @returns {boolean} - True if deletion was successful
 */
export const deleteLocalDeck = (deckId) => {
  try {
    const existingDecks = getLocalDecks();
    const filteredDecks = existingDecks.filter(d => d.localId !== deckId && d.id !== deckId);
    
    localStorage.setItem(DECKS_STORAGE_KEY, JSON.stringify(filteredDecks));
    return true;
  } catch (error) {
    console.error('Error deleting local deck:', error);
    return false;
  }
};

/**
 * Sync local decks to server (when online)
 * @param {Array} serverDecks - Array of decks from the server
 * @returns {Promise<Array>} - Array of synced deck results
 */
export const syncLocalDecksToServer = async (serverDecks) => {
  try {
    const localDecks = getLocalDecks();
    const results = [];
    
    // Filter out decks that are already on the server
    const unsyncedDecks = localDecks.filter(localDeck => 
      !serverDecks.some(serverDeck => serverDeck.name === localDeck.name && serverDeck.createdAt === localDeck.createdAt)
    );
    
    for (const localDeck of unsyncedDecks) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/decks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            name: localDeck.name,
            description: localDeck.description,
            cards: localDeck.cards
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          results.push({ localDeck, serverResult: result, success: true });
          
          // Remove the successfully synced deck from local storage
          deleteLocalDeck(localDeck.localId);
        } else {
          results.push({ localDeck, error: await response.text(), success: false });
        }
      } catch (error) {
        console.error(`Error syncing deck ${localDeck.name}:`, error);
        results.push({ localDeck, error: error.message, success: false });
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error during sync process:', error);
    return [];
  }
};